import { m } from "framer-motion";
import Image, { StaticImageData } from "next/image";
import React, { createElement, forwardRef, useMemo } from "react";
import { twMerge } from "tailwind-merge";

export type BgImageProps = React.JSX.IntrinsicElements["div"] & {
  wrapAs?: keyof React.JSX.IntrinsicElements;
  src: StaticImageData | string;
  alt: string;
  priority?: boolean;
  children?: React.ReactNode;
  wrapClassName?: string;
  className?: string;
  imageClassName?: string;
  /**
   * next/image bắt buộc `sizes` khi dùng `fill` (src là string, xem bên dưới) để
   * trình duyệt biết tải ảnh cỡ nào thay vì mặc định coi là full viewport.
   * BgImage vốn là ảnh nền full khung chứa nên 100vw là mặc định hợp lý; truyền
   * riêng khi khung chứa nhỏ hơn viewport (vd ảnh nền trong 1 modal hẹp).
   */
  sizes?: string;
};

const BgImage = forwardRef<HTMLElement, BgImageProps>(
  function BgImage(props, ref) {
    const {
      alt,
      src,
      wrapClassName,
      imageClassName,
      className,
      priority = true,
      children,
      wrapAs = "div",
      sizes = "100vw",
      ...rest
    } = props;

    const renderProps = useMemo(
      () => ({ className: twMerge("relative", wrapClassName), ref }),
      [wrapClassName, ref],
    );

    return createElement(
      wrapAs,
      renderProps,
      <>
        <Image
          alt={alt}
          src={src}
          priority={priority}
          fill={typeof src === "string"}
          sizes={typeof src === "string" ? sizes : undefined}
          className={twMerge("z-0", imageClassName)}
          quality={90}
        />
        {children && (
          <div {...rest} className={twMerge("absolute inset-0", className)}>
            {children}
          </div>
        )}
      </>,
    );
  },
);

export const MotionBgImage = m.create(BgImage);

export default BgImage;
